import { FC, useState } from 'react';
import { CircleMinus, Printer, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import ExitSlipDateField from './ExitSlipDateField';
import { ExitSlipPastTag } from '../../businessLogicService/exitSlipSnapshotHelper';

export enum ExitSlipRowVariant {
  //Assign panel: no date yet, printable, no cancel
  UNASSIGNED = 'UNASSIGNED',
  //Manage panel, Today & upcoming tab: reschedulable, cancellable, printable
  SCHEDULED = 'SCHEDULED',
  //Manage panel, Past tab: a record, not a control
  PAST = 'PAST',
  //Manage panel, Past tab: called off but still on file, so it can be put back on the calendar
  CANCELED = 'CANCELED',
}

interface Props {
  variant: ExitSlipRowVariant;
  unit: string;
  title: string;
  date: Date | null;
  blocked: ExitSlipPastTag[];
  pastTags: ExitSlipPastTag[];
  //the day the canceled ticket was due, reported in place of its date field
  canceledDate?: Date | null;
  //today's ticket is lifted out of the list and highlighted
  highlighted?: boolean;
  printDisabled?: boolean;
  onDateChange?: (day: Date) => void;
  onCancel?: () => void;
  onPrint?: () => void;
}

const actionClass =
  'tw-flex tw-h-10 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-none tw-bg-transparent tw-px-3 tw-py-2 tw-font-atkinson tw-text-sm tw-font-bold tw-leading-5 tw-text-indigo-600 hover:tw-bg-black/[0.04] disabled:tw-cursor-default disabled:tw-opacity-40';

const ExitSlipLessonRow: FC<Props> = ({
  variant,
  unit,
  title,
  date,
  blocked,
  pastTags,
  canceledDate,
  highlighted = false,
  printDisabled = false,
  onDateChange,
  onCancel,
  onPrint,
}) => {
  const canceled = variant === ExitSlipRowVariant.CANCELED;
  const [reassigning, setReassigning] = useState(false);

  return (
    <div
      className={`tw-flex tw-items-center tw-gap-6 tw-rounded-2xl tw-border-2 tw-px-5 ${
        canceled
          ? 'tw-border-dashed tw-border-neutral-200 tw-bg-white tw-py-3'
          : `tw-border-solid ${
              highlighted
                ? 'tw-border-indigo-200 tw-bg-indigo-25 tw-py-3'
                : 'tw-border-neutral-100 tw-bg-neutral-50 tw-py-5'
            }`
      }`}
    >
      <div
        className={`tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-start tw-justify-center ${
          highlighted ? 'tw-gap-1' : 'tw-gap-2'
        }`}
      >
        {unit && (
          <span
            className={`tw-rounded-md tw-border-solid tw-bg-white tw-px-2 tw-py-0.5 tw-font-atkinson tw-text-xs tw-font-bold tw-leading-4 tw-text-neutral-700 ${
              highlighted ? 'tw-border tw-border-indigo-100' : 'tw-border-2 tw-border-neutral-100'
            }`}
          >
            {unit}
          </span>
        )}
        <span className="tw-font-atkinson tw-text-base tw-font-bold tw-leading-5 tw-text-neutral-950">{title}</span>
      </div>

      <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-4">
        {canceled ? (
          <span className="tw-font-atkinson tw-text-sm tw-font-bold tw-leading-5 tw-text-neutral-700">
            {canceledDate ? `CANCELED ${format(canceledDate, 'MMM d').toUpperCase()}` : 'CANCELED'}
          </span>
        ) : (
          <span aria-hidden className="tw-h-7 tw-w-px tw-shrink-0 tw-bg-neutral-100" />
        )}

        {canceled && !reassigning ? (
          <button
            type="button"
            onClick={() => setReassigning(true)}
            className="tw-flex tw-h-10 tw-w-[140px] tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-3 tw-rounded-full tw-border-2 tw-border-solid tw-border-indigo-600 tw-bg-transparent tw-px-3 tw-py-2 tw-font-atkinson tw-text-sm tw-font-bold tw-leading-5 tw-text-indigo-600 hover:tw-bg-black/[0.04]"
          >
            <RotateCcw size={18} strokeWidth={2} />
            <span>Re-assign</span>
          </button>
        ) : (
          <ExitSlipDateField
            value={date}
            blocked={blocked}
            pastTags={pastTags}
            readOnly={variant === ExitSlipRowVariant.PAST}
            onChange={day => onDateChange?.(day)}
          />
        )}

        {variant === ExitSlipRowVariant.SCHEDULED && (
          <>
            <button type="button" onClick={onCancel} className={actionClass}>
              <CircleMinus size={16} strokeWidth={2} />
              <span>Cancel</span>
            </button>
            <button type="button" aria-label="Print" disabled={printDisabled} onClick={onPrint} className={actionClass}>
              <Printer size={16} strokeWidth={2} />
            </button>
          </>
        )}

        {variant === ExitSlipRowVariant.UNASSIGNED && (
          <button type="button" disabled={printDisabled} onClick={onPrint} className={actionClass}>
            <Printer size={16} strokeWidth={2} />
            <span>Print</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ExitSlipLessonRow;
